import { useEffect, useState } from 'react';
import { parseAsString, useQueryState } from 'nuqs';

import type {
  PatientIntakeStep,
  IntakeStatusCustomSteps,
  IntakeStatusBaseSteps,
  StatusActivityStatus,
} from '@shared/patient-intake-status';

import { usePatientIntakeStatusContext } from './usePatientIntakeStatusContext.ts';

type EditingStep = {
  step: PatientIntakeStep;
  status: StatusActivityStatus | undefined;
};

type GetEditingStepParams = {
  stepId: string | null;
  baseSteps: IntakeStatusBaseSteps | null;
  customSteps: IntakeStatusCustomSteps | null;
};

const getEditingStep = ({ stepId, baseSteps, customSteps }: GetEditingStepParams): EditingStep | null => {
  if (!baseSteps || !stepId || !customSteps) {
    return null;
  }

  const baseVerification = baseSteps[stepId as keyof IntakeStatusBaseSteps];

  if (baseVerification) {
    return {
      step: baseVerification,
      status: baseVerification.currentStatus.status,
    };
  }

  for (const step of Object.values(customSteps)) {
    for (const customStep of Object.values(step)) {
      if (customStep.id === stepId) {
        return {
          step: customStep,
          status: customStep.currentStatus.status,
        };
      }
    }
  }

  return null;
};

export const useStepEdit = () => {
  const { baseSteps, customSteps, isHealthcareProvider } = usePatientIntakeStatusContext();
  const [stepId, setStepId] = useQueryState(`pis-edit-stepId`, parseAsString);
  const [editingStep, setEditingStep] = useState<EditingStep | null>(null);

  useEffect(() => {
    if (!stepId || !isHealthcareProvider) {
      setEditingStep(null);

      return;
    }

    if (stepId && editingStep) {
      return;
    }

    setEditingStep(getEditingStep({ baseSteps, stepId, customSteps }));
  }, [baseSteps, stepId, customSteps]);

  const handleSetEditingStep = (step: string | null | PatientIntakeStep) => {
    if (!isHealthcareProvider) {
      return;
    }

    if (!step) {
      setStepId(null);
      setEditingStep(null);

      return;
    }

    if (typeof step === 'string') {
      setStepId(step);
      setEditingStep(getEditingStep({ baseSteps, customSteps, stepId: step }));

      return;
    }

    setStepId(step.id);

    setEditingStep({
      step,
      status: step.currentStatus.status,
    });
  };

  return {
    editingStep,
    setEditingStep: handleSetEditingStep,
  };
};
